import {
  type CopilotChatEntitlementQuotas,
  CopilotLicenseType,
  CopilotPlan,
  type UserLevelBudgetState,
} from '@github-ui/copilot-chat-types'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import type React from 'react'
import {createContext, use, useMemo} from 'react'

import {useCopilotChatEntitlement} from './copilot-chat-entitlement'
import {getTbbEntitlement} from './token-based-billing-entitlement'

// Telemetry-safe summary of the entitlement response shape, exposed for
// banner-impression and chat-input-hidden events. Booleans/counts only — no
// raw quota values — to keep PII review simple and analytics payloads stable.
export interface QuotaTelemetryShape {
  remainingChatPresent: boolean
  remainingChatValue: 'zero' | 'positive' | 'absent'
  remainingPremiumPresent: boolean
  quotasKeysCount: number
  hasResetDate: boolean
}

interface EntitlementContextType {
  licenseType: CopilotLicenseType | null
  plan: CopilotPlan | null
  chatQuotaRemaining: number
  premiumChatQuotaRemaining: number
  resetDate: string
  isLicensedLimited: boolean
  chatQuotaExceeded: boolean
  premiumInteractionsQuotaExceeded: boolean
  reloadQuota: (origin?: string, force?: boolean) => void
  canPurchaseAdditionalQuota: boolean
  canUpgradePlan: boolean
  overagesEnabled: boolean
  eligibleForTrial: boolean
  quotaExceeded: boolean
  quotaRemaining: number | undefined
  quotaResetAt: string | undefined | null
  quotaTotalAmount: number | null
  quotaUsedAmount: number | null
  tbbQuotaExceeded: boolean
  tokenBasedBillingEnabled: boolean
  userLevelBudgetEnabled: boolean
  userLevelBudgetState: UserLevelBudgetState | null
  userLevelBudgetPercentRemaining: number | null
  overagesRemaining: number | undefined
  overagesExceeded: boolean
  overagesBudgetAmount: number | null
  overagesBudgetSpend: number | null
  isCommercialPlan: boolean
  isIndividualPaidOrStudentPlan: boolean
  /**
   * When true, the user's Copilot license is still being issued or upgraded by
   * the licensing backend. Surfaced as the `licenseProvisioning` quota-banner
   * variant so users don't see misleading "quota exceeded" / "not authorized"
   * errors during the propagation window.
   */
  licenseProvisioning?: boolean
  quotaTelemetry?: QuotaTelemetryShape
}

interface EntitlementProviderProps {
  children: React.ReactNode
  initialLicenseType: CopilotLicenseType
  initialPlan?: CopilotPlan
  initialQuotas?: CopilotChatEntitlementQuotas
  hasCEorCBAccess?: boolean
  initialCanUpgradePlan?: boolean
}

export const EntitlementContext = createContext<EntitlementContextType | undefined>(undefined)

export function EntitlementProvider({
  children,
  initialLicenseType,
  initialPlan,
  initialQuotas,
  hasCEorCBAccess,
  initialCanUpgradePlan,
}: EntitlementProviderProps) {
  const [entitlement, reloadQuota] = useCopilotChatEntitlement(initialLicenseType, initialPlan, initialQuotas)
  const licenseType = entitlement.licenseType
  const plan = entitlement.plan ?? null

  // Feature flag to simulate quota exceeded state for local development
  const simulateQuotaExceeded = isFeatureEnabled('copilot_simulate_quota_exceeded')

  const isLicensedLimited = simulateQuotaExceeded || licenseType === CopilotLicenseType.LicensedLimited
  const effectiveHasCEorCBAccess = hasCEorCBAccess ?? (plan === CopilotPlan.Business || plan === CopilotPlan.Enterprise)

  const {
    overagesEnabled,
    tokenBasedBillingEnabled,
    userLevelBudgetEnabled,
    userLevelBudgetState,
    userLevelBudgetPercentRemaining,
    quotaRemaining,
    quotaExceeded,
    quotaTotalAmount,
    quotaUsedAmount,
    quotaResetAt,
    overagesRemaining,
    overagesExceeded,
    overagesBudgetAmount,
    overagesBudgetSpend,
    tbbQuotaExceeded,
    isCommercialPlan,
    isIndividualPaidOrStudentPlan,
  } = getTbbEntitlement({
    quotas: entitlement.quotas,
    plan,
    hasCEorCBAccess: effectiveHasCEorCBAccess,
    isLicensedLimited,
  })

  const chatQuotaRemaining = entitlement.chatQuotaRemaining()
  // When TBB is active and the server provides premiumInteractionsQuota, prefer its
  // percentRemaining over the legacy remaining.premiumInteractionsPercentage field.
  // The legacy field may be 0/absent even when the user still has quota.
  const premiumChatQuotaRemaining =
    tokenBasedBillingEnabled && entitlement.quotas?.premiumInteractionsQuota?.percentRemaining != null
      ? entitlement.quotas.premiumInteractionsQuota.percentRemaining
      : entitlement.premiumInteractionsQuotaRemaining()

  const resetDate = entitlement.quotas?.resetDate
    ? new Date(entitlement.quotas.resetDate).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : ''

  const canPurchaseAdditionalQuota = entitlement.canPurchaseAdditionalQuota()
  // When the server-side `copilot_user_can_upgrade_plan_field` flag is on, prefer the
  // authoritative value provided by the Rails payload (Copilot::Users::Access#can_upgrade_plan?),
  // which encodes the full plan-upgrade matrix (educational, max, CFB/CFE, etc.). Fall back to
  // the local heuristic for anyone the flag is off for, or when the field wasn't sent.
  const canUpgradePlan =
    copilotFeatureFlags.userCanUpgradePlanField && initialCanUpgradePlan !== undefined
      ? initialCanUpgradePlan
      : entitlement.canUpgradePlan()

  const chatQuotaExceeded = simulateQuotaExceeded || (isLicensedLimited && chatQuotaRemaining <= 0)
  // CB/CE TBB users have their own banner flow; suppress the legacy premium-interactions banner for them.
  const premiumInteractionsQuotaExceeded =
    copilotFeatureFlags.premiumRequestQuotasEnabled &&
    !isLicensedLimited &&
    !userLevelBudgetEnabled &&
    premiumChatQuotaRemaining <= 0

  const eligibleForTrial = entitlement.eligibleForTrial()

  const quotaTelemetry = useMemo<QuotaTelemetryShape>(() => {
    const remaining = entitlement.quotas?.remaining
    // Note: `remaining.chat` is typed as `number` but the backend can return
    // `remaining: {}` for replica-lag-affected new free signups (see
    // https://github.com/github/github-ui/pull/20407 and
    // https://github.com/github/copilot-experiences/issues/11688). Use `in`
    // checks for runtime-true presence rather than relying on the type.
    const chatPresent = remaining !== undefined && 'chat' in remaining
    let chatValue: 'zero' | 'positive' | 'absent' = 'absent'
    if (chatPresent && remaining) {
      chatValue = (remaining.chat ?? 0) > 0 ? 'positive' : 'zero'
    }
    return {
      remainingChatPresent: chatPresent,
      remainingChatValue: chatValue,
      remainingPremiumPresent: remaining !== undefined && 'premiumInteractions' in remaining,
      quotasKeysCount: remaining ? Object.keys(remaining).length : 0,
      hasResetDate: Boolean(entitlement.quotas?.resetDate),
    }
  }, [entitlement.quotas])

  const value = useMemo(
    () => ({
      licenseType,
      chatQuotaRemaining,
      premiumChatQuotaRemaining,
      resetDate,
      isLicensedLimited,
      chatQuotaExceeded,
      premiumInteractionsQuotaExceeded,
      reloadQuota,
      plan,
      canPurchaseAdditionalQuota,
      canUpgradePlan,
      overagesEnabled,
      eligibleForTrial,
      quotaExceeded,
      quotaRemaining,
      quotaTotalAmount,
      quotaUsedAmount,
      quotaResetAt,
      overagesRemaining,
      overagesExceeded,
      overagesBudgetAmount,
      overagesBudgetSpend,
      tbbQuotaExceeded,
      tokenBasedBillingEnabled,
      userLevelBudgetEnabled,
      userLevelBudgetState,
      userLevelBudgetPercentRemaining,
      isCommercialPlan,
      isIndividualPaidOrStudentPlan,
      licenseProvisioning: entitlement.licenseProvisioning,
      quotaTelemetry,
    }),
    [
      licenseType,
      chatQuotaRemaining,
      premiumChatQuotaRemaining,
      resetDate,
      isLicensedLimited,
      chatQuotaExceeded,
      premiumInteractionsQuotaExceeded,
      reloadQuota,
      plan,
      canPurchaseAdditionalQuota,
      canUpgradePlan,
      overagesEnabled,
      eligibleForTrial,
      quotaExceeded,
      quotaRemaining,
      quotaResetAt,
      quotaTotalAmount,
      quotaUsedAmount,
      overagesRemaining,
      overagesExceeded,
      overagesBudgetAmount,
      overagesBudgetSpend,
      tbbQuotaExceeded,
      tokenBasedBillingEnabled,
      userLevelBudgetEnabled,
      userLevelBudgetState,
      userLevelBudgetPercentRemaining,
      isCommercialPlan,
      isIndividualPaidOrStudentPlan,
      entitlement.licenseProvisioning,
      quotaTelemetry,
    ],
  )

  return <EntitlementContext value={value}>{children}</EntitlementContext>
}

export const useEntitlement = () => {
  const context = use(EntitlementContext)
  if (!context) {
    throw new Error('useEntitlement must be used within EntitlementProvider')
  }
  return context
}
